// 64×64 interface icons (iconKey.*). Clean line-icon style: white ~4 px strokes on
// transparent so the UI can tint them; orbs and danger nodes (elite, boss) carry
// their own colour.

import type Phaser from 'phaser';
import { COLORS, mix } from '../../config/palette';
import { ORB_TYPES, TAGS, type NodeType, type OrbType, type PassiveId, type SignatureId, type Tag, type UltimateId } from '../../core/types';
import { iconKey } from '../keys';
import { TAU, circlePath, hexPath, newCanvas, polyPath, rgba, softDot, type G } from './canvas';

const S = 64;
const LW = 4;
type Draw = (g: G) => void;

export function buildIcons(scene: Phaser.Scene): void {
  const slots: [Parameters<typeof iconKey.slot>[0], Draw][] = [
    ['chest', chest],
    ['shoulders', shoulders],
    ['back', back],
    ['legs', legs],
    ['weapon', weapon],
    ['mod', chip],
    ['mutation', helix],
  ];
  for (const [s, d] of slots) make(scene, iconKey.slot(s), d);
  for (const t of TAGS) make(scene, iconKey.tag(t), TAG_ICONS[t]);
  for (const [id, d] of Object.entries(SIGNATURES) as [SignatureId, Draw][]) make(scene, iconKey.signature(id), d);
  for (const [id, d] of Object.entries(ULTIMATES) as [UltimateId, Draw][]) make(scene, iconKey.ultimate(id), d);
  for (const [id, d] of Object.entries(PASSIVES) as [PassiveId, Draw][]) make(scene, iconKey.passive(id), d);
  for (const [id, d] of Object.entries(NODES) as [NodeType, Draw][]) make(scene, iconKey.node(id), d);
  for (const o of ORB_TYPES) make(scene, iconKey.orb(o), (g) => orbIcon(g, o));
  make(scene, iconKey.mod, chip);
  make(scene, iconKey.mutation, helix);
  make(scene, iconKey.upgrade, upgrade);
  make(scene, iconKey.scrap, gear);
  make(scene, iconKey.core, core);
  make(scene, iconKey.heat, thermometer);
  make(scene, iconKey.lock, lock);
}

function make(scene: Phaser.Scene, key: string, draw: Draw): void {
  const c = newCanvas(scene, key, S, S);
  if (!c) return;
  const g = c.g;
  g.strokeStyle = '#ffffff';
  g.fillStyle = '#ffffff';
  g.lineWidth = LW;
  g.lineCap = 'round';
  g.lineJoin = 'round';
  draw(g);
  c.tex.refresh();
}

// ───────────────────────────── Primitives ─────────────────────────────

function stroke(g: G, pts: readonly number[], close = false, w = LW): void {
  polyPath(g, pts, close);
  g.lineWidth = w;
  g.stroke();
}

function fillPoly(g: G, pts: readonly number[], alpha = 1): void {
  polyPath(g, pts, true);
  g.fillStyle = rgba(0xffffff, alpha);
  g.fill();
  g.fillStyle = '#ffffff';
}

function circle(g: G, x: number, y: number, r: number, w = LW): void {
  circlePath(g, x, y, r);
  g.lineWidth = w;
  g.stroke();
}

function dot(g: G, x: number, y: number, r: number, alpha = 1): void {
  circlePath(g, x, y, r);
  g.fillStyle = rgba(0xffffff, alpha);
  g.fill();
  g.fillStyle = '#ffffff';
}

function arc(g: G, x: number, y: number, r: number, a0: number, a1: number, w = LW): void {
  g.beginPath();
  g.arc(x, y, r, a0, a1);
  g.lineWidth = w;
  g.stroke();
}

/** Lightning bolt polygon centred around (cx, cy), scaled by s. */
function boltShape(g: G, cx: number, cy: number, s: number, filled = true): void {
  const p = [2, -22, -12, 3, -1, 3, -6, 22, 12, -4, 1, -4, 6, -22].map((v, i) => (i % 2 ? cy + v * s : cx + v * s));
  if (filled) fillPoly(g, p);
  else stroke(g, p, true);
}

function flameShape(g: G, cx: number, cy: number, s: number): void {
  g.beginPath();
  g.moveTo(cx, cy - 22 * s);
  g.bezierCurveTo(cx + 6 * s, cy - 12 * s, cx + 17 * s, cy - 6 * s, cx + 15 * s, cy + 8 * s);
  g.bezierCurveTo(cx + 13 * s, cy + 19 * s, cx - 13 * s, cy + 19 * s, cx - 15 * s, cy + 8 * s);
  g.bezierCurveTo(cx - 16 * s, cy - 2 * s, cx - 9 * s, cy - 6 * s, cx - 7 * s, cy - 14 * s);
  g.bezierCurveTo(cx - 3 * s, cy - 8 * s, cx - 2 * s, cy - 14 * s, cx, cy - 22 * s);
  g.closePath();
  g.lineWidth = LW;
  g.stroke();
  // Inner tongue.
  g.beginPath();
  g.moveTo(cx, cy - 2 * s);
  g.bezierCurveTo(cx + 6 * s, cy + 4 * s, cx + 6 * s, cy + 13 * s, cx, cy + 13 * s);
  g.bezierCurveTo(cx - 6 * s, cy + 13 * s, cx - 6 * s, cy + 6 * s, cx, cy - 2 * s);
  g.fill();
}

function shieldShape(g: G, cx: number, cy: number, s: number): void {
  g.beginPath();
  g.moveTo(cx, cy - 22 * s);
  g.lineTo(cx + 18 * s, cy - 15 * s);
  g.quadraticCurveTo(cx + 18 * s, cy + 12 * s, cx, cy + 23 * s);
  g.quadraticCurveTo(cx - 18 * s, cy + 12 * s, cx - 18 * s, cy - 15 * s);
  g.closePath();
  g.lineWidth = LW;
  g.stroke();
}

// ───────────────────────────── Slots ─────────────────────────────

function chest(g: G): void {
  // Torso plate with collar and centre seam.
  stroke(g, [18, 12, 46, 12, 54, 22, 50, 44, 40, 54, 24, 54, 14, 44, 10, 22], true);
  stroke(g, [24, 12, 32, 20, 40, 12]);
  stroke(g, [32, 20, 32, 46]);
  stroke(g, [18, 30, 26, 34]);
  stroke(g, [46, 30, 38, 34]);
}

function shoulders(g: G): void {
  // Layered pauldron.
  g.beginPath();
  g.moveTo(10, 40);
  g.quadraticCurveTo(12, 12, 38, 12);
  g.quadraticCurveTo(54, 12, 56, 28);
  g.lineTo(56, 40);
  g.closePath();
  g.lineWidth = LW;
  g.stroke();
  arc(g, 34, 44, 18, Math.PI * 1.05, Math.PI * 1.75);
  stroke(g, [14, 50, 52, 50]);
  dot(g, 44, 24, 3);
}

function back(g: G): void {
  // Twin thruster pack.
  for (const x of [16, 36]) {
    g.beginPath();
    g.roundRect(x, 10, 13, 32, 5);
    g.lineWidth = LW;
    g.stroke();
    stroke(g, [x + 2, 42, x + 1, 47, x + 12, 47, x + 11, 42]);
    stroke(g, [x + 6.5, 51, x + 6.5, 57], false, 3);
  }
  stroke(g, [29, 20, 36, 20]);
  stroke(g, [29, 32, 36, 32]);
}

function legs(g: G): void {
  // Mech leg: hip, thigh, knee joint, shin, foot.
  stroke(g, [24, 8, 40, 8, 38, 26, 30, 28, 22, 24], true);
  circle(g, 32, 32, 5);
  stroke(g, [28, 36, 24, 50, 38, 50, 36, 36]);
  stroke(g, [18, 56, 46, 56, 42, 50, 22, 50], true);
}

function weapon(g: G): void {
  // Side view rifle.
  stroke(g, [6, 26, 40, 26, 40, 22, 52, 22, 52, 26, 58, 26, 58, 32, 40, 32, 34, 38, 24, 38, 20, 46, 12, 46, 14, 34, 6, 34], true);
  stroke(g, [24, 26, 24, 20, 34, 20, 34, 26]);
  dot(g, 46, 29, 2);
}

function chip(g: G): void {
  g.beginPath();
  g.roundRect(17, 17, 30, 30, 4);
  g.lineWidth = LW;
  g.stroke();
  for (let i = 0; i < 3; i++) {
    const o = 24 + i * 8;
    stroke(g, [o, 9, o, 17], false, 3);
    stroke(g, [o, 47, o, 55], false, 3);
    stroke(g, [9, o, 17, o], false, 3);
    stroke(g, [47, o, 55, o], false, 3);
  }
  fillPoly(g, [26, 26, 38, 26, 38, 38, 26, 38], 0.85);
}

function helix(g: G): void {
  // Double helix with rungs (mutation).
  g.lineWidth = LW;
  for (const ph of [0, Math.PI]) {
    g.beginPath();
    for (let i = 0; i <= 24; i++) {
      const t = i / 24;
      const y = 8 + t * 48;
      const x = 32 + Math.sin(t * TAU * 1.2 + ph) * 14;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.stroke();
  }
  for (let i = 1; i < 6; i++) {
    const t = i / 6;
    const y = 8 + t * 48;
    const dx = Math.sin(t * TAU * 1.2) * 14;
    if (Math.abs(dx) < 4) continue;
    stroke(g, [32 - dx * 0.75, y, 32 + dx * 0.75, y], false, 2.5);
  }
}

// ───────────────────────────── Tags ─────────────────────────────

const TAG_ICONS: Record<Tag, Draw> = {
  electric: (g) => boltShape(g, 32, 32, 1.15),
  burn: (g) => flameShape(g, 32, 34, 1.05),
  dash(g) {
    stroke(g, [30, 18, 46, 32, 30, 46]);
    stroke(g, [44, 18, 58, 32, 44, 46]);
    stroke(g, [6, 24, 22, 24]);
    stroke(g, [10, 32, 26, 32]);
    stroke(g, [6, 40, 22, 40]);
  },
  shield(g) {
    shieldShape(g, 32, 31, 1.05);
    stroke(g, [32, 16, 32, 46], false, 3);
  },
  explosive(g) {
    const pts: number[] = [];
    for (let i = 0; i < 16; i++) {
      const a = (i / 16) * TAU - Math.PI / 2;
      const r = i % 2 ? 11 : i % 4 === 0 ? 26 : 19;
      pts.push(32 + Math.cos(a) * r, 33 + Math.sin(a) * r);
    }
    stroke(g, pts, true);
    dot(g, 32, 33, 5);
  },
  gravity(g) {
    dot(g, 32, 32, 6);
    g.save();
    g.translate(32, 32);
    g.rotate(-0.45);
    g.beginPath();
    g.ellipse(0, 0, 25, 10, 0, 0, TAU);
    g.lineWidth = LW;
    g.stroke();
    g.restore();
    arc(g, 32, 32, 16, -2.6, -0.6, 3);
  },
  kinetic(g) {
    // Slug with impact lines.
    stroke(g, [10, 26, 34, 26, 44, 32, 34, 38, 10, 38], true);
    stroke(g, [16, 26, 16, 38], false, 3);
    stroke(g, [50, 22, 56, 16]);
    stroke(g, [52, 32, 60, 32]);
    stroke(g, [50, 42, 56, 48]);
  },
  precision(g) {
    circle(g, 32, 32, 17);
    stroke(g, [32, 6, 32, 20]);
    stroke(g, [32, 44, 32, 58]);
    stroke(g, [6, 32, 20, 32]);
    stroke(g, [44, 32, 58, 32]);
    dot(g, 32, 32, 3.5);
  },
  heat: thermometer,
  missile(g) {
    g.save();
    g.translate(32, 32);
    g.rotate(-Math.PI / 4);
    stroke(g, [-14, -6, 10, -6, 20, 0, 10, 6, -14, 6], true);
    stroke(g, [-14, -6, -20, -13, -20, -6]);
    stroke(g, [-14, 6, -20, 13, -20, 6]);
    stroke(g, [-24, 0, -30, 0], false, 3);
    g.restore();
  },
  chain(g) {
    g.save();
    g.translate(32, 32);
    g.rotate(-Math.PI / 4);
    for (const x of [-11, 11]) {
      g.beginPath();
      g.roundRect(x - 13, -8, 26, 16, 8);
      g.lineWidth = LW;
      g.stroke();
    }
    g.restore();
  },
};

function thermometer(g: G): void {
  g.beginPath();
  g.moveTo(26, 40);
  g.lineTo(26, 14);
  g.arc(32, 14, 6, Math.PI, 0);
  g.lineTo(38, 40);
  g.lineWidth = LW;
  g.stroke();
  circle(g, 32, 47, 10);
  dot(g, 32, 47, 5.5);
  stroke(g, [32, 22, 32, 44], false, 4);
  for (const y of [18, 26, 34]) stroke(g, [44, y, 50, y], false, 3);
}

// ───────────────────────────── Abilities ─────────────────────────────

const SIGNATURES: Record<SignatureId, Draw> = {
  arc_dash(g) {
    // Forward arrow with an arc trail.
    stroke(g, [36, 16, 54, 32, 36, 48]);
    stroke(g, [6, 32, 12, 24, 18, 38, 24, 26, 30, 36, 36, 32, 52, 32]);
  },
  siege_field(g) {
    // Dome field over a base with a hex core.
    arc(g, 32, 46, 24, Math.PI, TAU);
    stroke(g, [4, 46, 60, 46]);
    hexPath(g, 32, 36, 8);
    g.lineWidth = 3;
    g.stroke();
    for (const x of [14, 50]) stroke(g, [x, 52, x, 56], false, 3);
  },
  phase_echo(g) {
    // A mech silhouette (head + torso + legs) leaving two fading echoes behind it.
    const fig = (dx: number, a: number) => {
      g.strokeStyle = rgba(0xffffff, a);
      circle(g, 36 + dx, 14, 5, 3.5);
      stroke(g, [28 + dx, 22, 44 + dx, 22, 42 + dx, 38, 30 + dx, 38], true, 3.5);
      stroke(g, [31 + dx, 38, 28 + dx, 54], false, 3.5);
      stroke(g, [41 + dx, 38, 44 + dx, 54], false, 3.5);
    };
    fig(-20, 0.25);
    fig(-10, 0.5);
    fig(4, 1);
    g.strokeStyle = '#ffffff';
  },
};

const ULTIMATES: Record<UltimateId, Draw> = {
  overload(g) {
    circle(g, 32, 32, 9);
    dot(g, 32, 32, 4);
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * TAU;
      const c = Math.cos(a);
      const s = Math.sin(a);
      const n = -s;
      const m = c;
      stroke(g, [32 + c * 15, 32 + s * 15, 32 + c * 21 + n * 4, 32 + s * 21 + m * 4, 32 + c * 27, 32 + s * 27], false, 3);
    }
  },
  fortress_protocol(g) {
    // Battlement wall with a shield emblem.
    stroke(g, [8, 54, 8, 18, 16, 18, 16, 24, 24, 24, 24, 18, 40, 18, 40, 24, 48, 24, 48, 18, 56, 18, 56, 54], true);
    shieldShape(g, 32, 39, 0.5);
  },
  eclipse(g) {
    circle(g, 32, 32, 18);
    g.save();
    circlePath(g, 32, 32, 18);
    g.clip();
    circlePath(g, 40, 26, 17);
    g.fill();
    g.restore();
    for (let i = 0; i < 12; i++) {
      const a = (i / 12) * TAU;
      stroke(g, [32 + Math.cos(a) * 24, 32 + Math.sin(a) * 24, 32 + Math.cos(a) * 28, 32 + Math.sin(a) * 28], false, 3);
    }
  },
};

const PASSIVES: Record<PassiveId, Draw> = {
  live_wire(g) {
    // Coiled wire with a spark at the end.
    g.beginPath();
    for (let i = 0; i <= 40; i++) {
      const t = i / 40;
      const x = 8 + t * 38;
      const y = 40 + Math.sin(t * TAU * 2.5) * 8 - t * 12;
      if (i === 0) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    g.lineWidth = LW;
    g.stroke();
    boltShape(g, 50, 20, 0.5);
  },
  bulwark_frame(g) {
    // Armour frame with heavy corners.
    g.beginPath();
    g.rect(14, 14, 36, 36);
    g.lineWidth = 2.5;
    g.stroke();
    for (const [x, y, sx, sy] of [
      [10, 10, 1, 1],
      [54, 10, -1, 1],
      [10, 54, 1, -1],
      [54, 54, -1, -1],
    ]) {
      stroke(g, [x, y + sy * 14, x, y, x + sx * 14, y], false, 6);
    }
    dot(g, 32, 32, 5);
  },
  ghost_drive(g) {
    // Dashed afterimage trail leading into a solid chevron.
    g.setLineDash([5, 6]);
    stroke(g, [8, 20, 26, 32, 8, 44], false, 3);
    g.setLineDash([]);
    g.strokeStyle = rgba(0xffffff, 0.55);
    stroke(g, [22, 18, 38, 32, 22, 46]);
    g.strokeStyle = '#ffffff';
    stroke(g, [36, 16, 54, 32, 36, 48]);
  },
};

// ───────────────────────────── Route nodes ─────────────────────────────

const NODES: Record<NodeType, Draw> = {
  start(g) {
    stroke(g, [18, 56, 18, 8]);
    stroke(g, [18, 10, 48, 16, 18, 30]);
    fillPoly(g, [18, 10, 48, 16, 18, 30], 0.35);
  },
  combat(g) {
    // Crossed blades.
    stroke(g, [12, 12, 46, 46]);
    stroke(g, [52, 12, 18, 46]);
    stroke(g, [40, 50, 50, 40]);
    stroke(g, [14, 40, 24, 50]);
    stroke(g, [46, 46, 54, 54]);
    stroke(g, [18, 46, 10, 54]);
  },
  elite(g) {
    // Spiked crown in threat colour (intrinsic: danger).
    const col = COLORS.threatHot;
    softDot(g, 32, 34, 30, col, 0.25);
    g.strokeStyle = rgba(col);
    stroke(g, [10, 46, 8, 18, 22, 30, 32, 10, 42, 30, 56, 18, 54, 46], true);
    stroke(g, [10, 54, 54, 54]);
    dot(g, 32, 38, 4);
    g.fillStyle = rgba(col);
    circlePath(g, 32, 38, 4);
    g.fill();
  },
  loot(g) {
    // Orb with a sparkle.
    circle(g, 30, 34, 16);
    arc(g, 30, 34, 10, Math.PI * 1.1, Math.PI * 1.5, 3);
    fillPoly(g, [50, 6, 53, 13, 60, 16, 53, 19, 50, 26, 47, 19, 40, 16, 47, 13]);
  },
  repair(g) {
    // Open-jaw wrench, diagonal, with a small plus.
    const jaw = -Math.PI * 0.75;
    g.beginPath();
    g.arc(20, 20, 11, jaw + 0.75, jaw - 0.75 + TAU);
    g.lineWidth = LW;
    g.stroke();
    stroke(g, [28, 28, 48, 48]);
    stroke(g, [44, 52, 52, 44], false, 6);
    stroke(g, [46, 14, 58, 14], false, 3.5);
    stroke(g, [52, 8, 52, 20], false, 3.5);
  },
  station(g) {
    // Kiosk with awning.
    stroke(g, [8, 24, 14, 12, 50, 12, 56, 24], true);
    stroke(g, [12, 24, 12, 54, 52, 54, 52, 24]);
    g.beginPath();
    g.rect(22, 34, 20, 20);
    g.lineWidth = 3;
    g.stroke();
    for (const x of [20, 32, 44]) stroke(g, [x, 12, x - 2, 24], false, 2.5);
  },
  boss(g) {
    // Mech skull in threat colour (intrinsic: danger).
    const col = COLORS.threat;
    softDot(g, 32, 32, 30, col, 0.3);
    g.strokeStyle = rgba(col);
    g.fillStyle = rgba(col);
    stroke(g, [14, 30, 18, 12, 46, 12, 50, 30, 46, 40, 44, 52, 20, 52, 18, 40], true);
    fillPoly2(g, [20, 28, 30, 30, 28, 36, 20, 34], col);
    fillPoly2(g, [44, 28, 34, 30, 36, 36, 44, 34], col);
    for (const x of [26, 32, 38]) stroke(g, [x, 44, x, 52], false, 2.5);
  },
};

function fillPoly2(g: G, pts: readonly number[], col: number): void {
  polyPath(g, pts, true);
  g.fillStyle = rgba(col);
  g.fill();
}

// ───────────────────────────── Orbs / misc ─────────────────────────────

function orbIcon(g: G, type: OrbType): void {
  const col = type === 'arsenal' ? COLORS.orbArsenal : type === 'armor' ? COLORS.orbArmor : COLORS.orbUnstable;
  softDot(g, 32, 32, 32, col, 0.35);
  circlePath(g, 32, 32, 17);
  const grad = g.createRadialGradient(27, 25, 2, 32, 32, 17);
  grad.addColorStop(0, rgba(mix(col, 0xffffff, 0.55)));
  grad.addColorStop(0.6, rgba(mix(col, COLORS.ink, 0.3)));
  grad.addColorStop(1, rgba(mix(col, COLORS.ink, 0.6)));
  g.fillStyle = grad;
  g.fill();
  g.strokeStyle = rgba(col);
  g.lineWidth = 3;
  if (type === 'arsenal') {
    arc(g, 32, 32, 24, 0.3, Math.PI / 2 - 0.3, 3);
    arc(g, 32, 32, 24, Math.PI / 2 + 0.3, Math.PI - 0.3, 3);
    arc(g, 32, 32, 24, Math.PI + 0.3, Math.PI * 1.5 - 0.3, 3);
    arc(g, 32, 32, 24, Math.PI * 1.5 + 0.3, TAU - 0.3, 3);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * TAU;
      stroke(g, [32 + Math.cos(a) * 21, 32 + Math.sin(a) * 21, 32 + Math.cos(a) * 29, 32 + Math.sin(a) * 29], false, 3);
    }
  } else if (type === 'armor') {
    hexPath(g, 32, 32, 26, Math.PI / 6);
    g.lineWidth = 3.5;
    g.stroke();
  } else {
    for (let i = 0; i < 7; i++) {
      const a0 = (i / 7) * TAU;
      arc(g, 32 + (i % 2 ? 1.5 : -1.5), 32, 24 + (i % 3), a0, a0 + TAU / 7 - 0.35, 3);
    }
  }
  // Specular.
  circlePath(g, 26, 25, 4);
  g.fillStyle = 'rgba(255,255,255,0.85)';
  g.fill();
}

function upgrade(g: G): void {
  stroke(g, [16, 30, 32, 14, 48, 30]);
  stroke(g, [16, 44, 32, 28, 48, 44]);
  stroke(g, [18, 54, 46, 54]);
}

function gear(g: G): void {
  const pts: number[] = [];
  const teeth = 8;
  for (let i = 0; i < teeth * 4; i++) {
    const a = (i / (teeth * 4)) * TAU;
    const r = i % 4 < 2 ? 24 : 18;
    pts.push(32 + Math.cos(a) * r, 32 + Math.sin(a) * r);
  }
  stroke(g, pts, true, 3.5);
  circle(g, 32, 32, 8);
}

function core(g: G): void {
  // Faceted crystal (meta currency).
  stroke(g, [32, 6, 50, 22, 44, 54, 20, 54, 14, 22], true);
  stroke(g, [14, 22, 50, 22], false, 3);
  stroke(g, [24, 22, 32, 54, 40, 22], false, 3);
  stroke(g, [24, 22, 32, 6, 40, 22], false, 3);
  fillPoly(g, [24, 22, 40, 22, 32, 54], 0.3);
}

function lock(g: G): void {
  g.beginPath();
  g.moveTo(21, 28);
  g.lineTo(21, 20);
  g.arc(32, 20, 11, Math.PI, 0);
  g.lineTo(43, 28);
  g.lineWidth = LW;
  g.stroke();
  g.beginPath();
  g.roundRect(14, 28, 36, 28, 4);
  g.stroke();
  dot(g, 32, 39, 4);
  stroke(g, [32, 42, 32, 48], false, 4);
}
